"use client";

import { Check, Clock, Sparkles, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const traditionalItems = [{
    text: "High corrosion risk"
  }, {
    text: "Frequent replacement costs"
  }, {
    text: "Standard aesthetic finish"
  }, {
    text: "Limited chemical resistance"
  }, {
    text: "Requires regular maintenance"
  }, {
    text: "Prone to surface staining"
  }];
  const aiItems = [{
    text: "Superior corrosion resistance"
  }, {
    text: "Long-term cost efficiency"
  }, {
    text: "Premium industrial finish"
  }, {
    text: "High chemical durability"
  }, {
    text: "Low maintenance requirements"
  }, {
    text: "Stain-resistant surface"
  }];
  return <section data-section-id="114" id="whentochoosestainless" className="relative overflow-hidden bg-background py-20 sm:py-28 text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-lg blur-3xl" style={{
        background: "radial-gradient(circle, hsla(45, 68%, 47%, 0.15) 0%, transparent 70%)"
      }} />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Badge variant="outline" className="mb-6 border-border bg-card px-3 py-1 text-xs font-mono tracking-widest text-muted-foreground">
            MATERIAL SELECTION
          </Badge>
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Why choose{" "}
            <span className="bg-clip-text text-transparent" style={{
            backgroundImage: "linear-gradient(90deg, hsl(45 68% 47%), hsl(40 21% 70%))"
          }}>
              Stainless Steel
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Compare the durability and performance of our premium stainless steel solutions against standard industrial materials.
          </p>
        </div>

        {}
        <div className="relative mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-6 lg:gap-10">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 hidden h-full -translate-x-1/2 md:flex md:flex-col md:items-center md:justify-center z-10">
            <div className="h-full w-px bg-gradient-to-b from-transparent via-border to-transparent" />
            <div className="absolute flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-background font-mono text-sm font-bold text-foreground shadow-md" style={{
            backgroundImage: "linear-gradient(135deg, hsla(45, 68%, 47%, 0.1), hsla(216, 40%, 20%, 0.1))"
          }}>
              VS
            </div>
          </div>

          {}
          <div ref={leftRef} className={`relative transition-all duration-700 ${leftVisible ? "opacity-70 translate-x-0" : "opacity-0 -translate-x-6"}`}>
            <div className="relative rounded-lg border border-border bg-card p-8 shadow-md">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-border bg-muted">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                </div>
                <h3 className="font-default text-xl font-semibold text-muted-foreground">
                  Standard Materials
                </h3>
              </div>

              <ul className="space-y-4">
                {traditionalItems.map((item, i) => <li data-index={i} key={i} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10">
                      <X className="h-3.5 w-3.5 text-destructive" />
                    </div>
                    <span className="text-sm sm:text-base text-muted-foreground">
                      {item.text}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative transition-all duration-700 md:scale-[1.03] ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
            {}
            <div aria-hidden="true" className="absolute -inset-px rounded-lg opacity-90 blur-sm" style={{
            background: "linear-gradient(135deg, hsl(45 68% 47%), hsl(40 21% 70%))"
          }} />

            <div className="relative rounded-lg border border-border p-8 shadow-lg bg-card">
              {}
              <div className="absolute -top-3 right-6">
                <Badge className="border-0 px-3 py-1 font-mono text-[10px] font-semibold tracking-widest text-primary-foreground shadow-md" style={{
                backgroundImage: "linear-gradient(90deg, hsl(45 68% 47%), hsl(40 21% 70%))"
              }}>
                  PREMIUM
                </Badge>
              </div>

              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md shadow-md bg-primary">
                  <Sparkles className="h-5 w-5 text-primary-foreground" />
                </div>
                <h3 className="font-default text-xl font-semibold text-foreground">
                  Stainless Steel
                </h3>
              </div>

              <ul className="space-y-4">
                {aiItems.map((item, i) => <li data-index={i} key={i} className="flex items-center gap-3">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md shadow bg-primary">
                      <Check className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
                    </div>
                    <span className="text-sm sm:text-base text-foreground">
                      {item.text}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}